import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { showToast } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import { CreditUsageAnalyticsApi } from 'entities/Analytics/api/creditUsageAnalytics'
import {
  type IParamsCreditUsageSourcesList,
  type IParamsCreditUsageSourcesSummary,
  type IResponseCreditUsageSourcesSummary,
} from 'entities/Analytics/api/types'

export class SourcesSummaryStore {
  private _summary: IResponseCreditUsageSourcesSummary | null = null
  private _loading = false
  private _disposeReloadSummary: IReactionDisposer | null = null

  constructor(
    private _analyticsFiltersParams: () => Pick<
      IParamsCreditUsageSourcesList,
      'from' | 'to' | 'timezone'
    >,
    private _selectedSourceGroups: () => string[]
  ) {
    makeAutoObservable(this)
  }

  init = () => {
    this.loadSummary()
    this.reactionReloadSummary()
  }

  reactionReloadSummary = () => {
    this._disposeReloadSummary?.()
    this._disposeReloadSummary = reaction(
      () => this.paramsSummary,
      () => {
        this._requestLoadSummary.debounceLoadData()
      }
    )
  }

  get summary() {
    return this._summary
  }

  get loading() {
    return this._loading
  }

  get paramsSummary(): IParamsCreditUsageSourcesSummary {
    const { from, to, timezone } = this._analyticsFiltersParams()
    const sourceGroup = this._selectedSourceGroups()

    return {
      from,
      to,
      timezone,
      ...(sourceGroup.length ? { source_group: sourceGroup } : {}),
    }
  }

  private _requestLoadSummary = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await CreditUsageAnalyticsApi.getCreditUsageSourcesSummary(
        this.paramsSummary,
        { cancelToken: cancelRequest.token }
      )

      runInAction(() => {
        this._summary = data
      })
    },
    errorCallback: () => {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._loading = false
      })
    },
  })

  loadSummary = async () => {
    return this._requestLoadSummary.loadData()
  }

  dispose = () => {
    this._disposeReloadSummary?.()
    this._requestLoadSummary.dispose()
  }
}
